import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-[#FBFAF6] text-[#3D4B59]">
      <Helmet>
        <title>Terms of Service - Cosmovex</title>
        <meta
          name="description"
          content="Terms of Service for Cosmovex - Read our terms and conditions for using our website."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://cosmovexapps.com/terms-of-service" />
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FBFAF6]/80 backdrop-blur-xl border-b border-[#E1DCCE]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="w-10 h-10 bg-[#E8B44C] border border-[#D9A32E] rounded-xl flex items-center justify-center shadow-sm transition-shadow">
                <span className="text-lg font-black text-[#221A06]">C</span>
              </div>
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#26313B] group-hover:text-[#A8720E] transition-colors">
                Cosmovex
              </h1>
              <p className="text-xs text-[#8A93A2] font-mono">// Tech Insights</p>
            </div>
          </Link>

          <nav className="flex items-center gap-2">
            <Link
              to="/"
              className="px-4 py-2 text-sm text-[#5B6B7C] hover:text-[#26313B] hover:bg-[#F1EEE5] rounded-lg transition-all"
            >
              Home
            </Link>
            <Link
              to="/blogs"
              className="px-4 py-2 text-sm text-[#5B6B7C] hover:text-[#26313B] hover:bg-[#F1EEE5] rounded-lg transition-all"
            >
              Blogs
            </Link>
          </nav>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-[#26313B]">Terms of Service</h1>
          <p className="text-[#5B6B7C] mb-8">
            Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </p>

          <div className="prose max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">1. Acceptance of Terms</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                Welcome to Cosmovex. By accessing or using our website at cosmovexapps.com (the "Website"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Website.
              </p>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                We reserve the right to modify these Terms at any time. Your continued use of the Website after any changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">2. Description of Service</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                Cosmovex provides technology-related content, including blog posts, articles, tutorials, and insights about AI, software development, and modern technology. We also develop and showcase mobile applications.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">3. User Conduct</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                When using our Website, you agree to:
              </p>
              <ul className="list-disc list-inside text-[#3D4B59] space-y-2 mb-4">
                <li>Use the Website only for lawful purposes</li>
                <li>Not engage in any activity that disrupts or interferes with the Website</li>
                <li>Not attempt to gain unauthorized access to any part of the Website</li>
                <li>Not transmit any viruses, malware, or harmful code</li>
                <li>Not violate any applicable laws or regulations</li>
                <li>Not impersonate any person or entity</li>
                <li>Respect the intellectual property rights of others</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">4. Intellectual Property Rights</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                All content on the Website, including but not limited to text, graphics, logos, images, code, and software, is the property of Cosmovex or its content suppliers and is protected by international copyright, trademark, and other intellectual property laws.
              </p>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                You may view, download, and print content from the Website for personal, non-commercial use only. You may not modify, reproduce, distribute, or create derivative works without our prior written permission.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">5. User-Generated Content</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                If you submit comments, feedback, or other content to our Website, you grant Cosmovex a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display such content.
              </p>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                You represent and warrant that:
              </p>
              <ul className="list-disc list-inside text-[#3D4B59] space-y-2 mb-4">
                <li>You own or have the necessary rights to the content you submit</li>
                <li>Your content does not violate any third-party rights</li>
                <li>Your content is not defamatory, offensive, or illegal</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">6. Third-Party Links and Content</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                Our Website may contain links to third-party websites or services. We do not control, endorse, or assume responsibility for any third-party content. Your use of third-party websites is at your own risk and subject to their terms and conditions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">7. Advertising</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                Our Website displays advertisements through Google AdSense and other advertising networks. We are not responsible for the content of these advertisements. Clicking on ads may take you to third-party websites, which are governed by their own terms and privacy policies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">8. Disclaimer of Warranties</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                THE WEBSITE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc list-inside text-[#3D4B59] space-y-2 mb-4">
                <li>Warranties of merchantability or fitness for a particular purpose</li>
                <li>Warranties that the Website will be uninterrupted, error-free, or secure</li>
                <li>Warranties regarding the accuracy, reliability, or completeness of content</li>
              </ul>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                We do not warrant that any content on the Website is accurate, complete, or current. The content is provided for informational purposes only and should not be relied upon as professional advice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">9. Limitation of Liability</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, COSMOVEX SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc list-inside text-[#3D4B59] space-y-2 mb-4">
                <li>Loss of profits, data, or goodwill</li>
                <li>Service interruption</li>
                <li>Computer damage or system failure</li>
                <li>Cost of substitute services</li>
              </ul>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                This limitation applies regardless of the legal theory (contract, tort, or otherwise) and even if we have been advised of the possibility of such damages.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">10. Indemnification</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                You agree to indemnify, defend, and hold harmless Cosmovex, its affiliates, and their respective officers, directors, employees, and agents from any claims, losses, damages, liabilities, and expenses (including reasonable attorneys' fees) arising out of or related to:
              </p>
              <ul className="list-disc list-inside text-[#3D4B59] space-y-2 mb-4">
                <li>Your use of the Website</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any rights of another party</li>
                <li>Your content or submissions</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">11. Privacy</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                Your use of the Website is also governed by our{" "}
                <Link to="/privacy-policy" className="text-[#0F6BBF] hover:text-[#0C599F] underline">
                  Privacy Policy
                </Link>
                , which explains how we collect, use, and protect your personal information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">12. Termination</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                We reserve the right to terminate or suspend your access to the Website at any time, without notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason at our sole discretion.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">13. Governing Law</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of the Website shall be subject to the exclusive jurisdiction of the courts located in India.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">14. Severability</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">15. Entire Agreement</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and Cosmovex regarding your use of the Website.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">16. Contact Information</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className="bg-[#F1EEE5] border border-[#E1DCCE] rounded-lg p-6">
                <p className="text-[#3D4B59]">
                  <strong>Email:</strong>{" "}
                  <a href="mailto:contact@cosmovex.com" className="text-[#0F6BBF] hover:text-[#0C599F]">
                    contact@cosmovex.com
                  </a>
                </p>
                <p className="text-[#3D4B59] mt-2">
                  <strong>Website:</strong>{" "}
                  <a href="https://cosmovexapps.com" className="text-[#0F6BBF] hover:text-[#0C599F]">
                    https://cosmovexapps.com
                  </a>
                </p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#E1DCCE] py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-[#8A93A2] text-sm">
            &copy; {new Date().getFullYear()} Cosmovex. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/privacy-policy" className="text-[#5B6B7C] hover:text-[#A8720E] text-sm">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="text-[#5B6B7C] hover:text-[#A8720E] text-sm">
              Terms of Service
            </Link>
            <Link to="/about" className="text-[#5B6B7C] hover:text-[#A8720E] text-sm">
              About
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
